import { useEffect, useState } from "react";

import { ValueSetExpansionContains } from "@medplum/fhirtypes";
import { useMedplum } from "@medplum/react";

import { NativeSelect, Stack, Textarea, TextInput } from "@mantine/core";
import type { UseFormReturnType } from "@mantine/form";

import {
  ClearableYesNoRadioField,
  isOtherDisplaySelected,
  OTHER_DISPLAY,
  yesNoOtherRadioOptions,
} from "@/components/ClearableYesNoRadioField";
import { DateTimePickerNowField } from "@/components/DateTimePickerNowField";
import BirthingPostpartumFlowsheetNoticeField from "@/encounters/components/EncounterLabor/EncounterLabor/LaborFlowsheetComponents/BirthingPostpartumFlowsheetNoticeField";
import ShoulderDystociaDisplay from "@/encounters/components/EncounterLabor/EncounterLabor/LaborFlowsheetComponents/ShoulderDystociaDisplay";
import {
  BIRTHING_BODY_DATE_TIME_LINK_ID,
  BIRTHING_CROWN_DATE_TIME_LINK_ID,
  BIRTHING_DATE_LINK_ID,
  BIRTHING_DATE_TIME_LINK_ID,
  BIRTHING_DELIVERY_METHOD_LINK_ID,
  BIRTHING_DELIVERY_METHOD_OTHER_LINK_ID,
  BIRTHING_FETAL_PRESENTATION_LINK_ID,
  BIRTHING_HEAD_DATE_TIME_LINK_ID,
  BIRTHING_IN_WATER_LINK_ID,
  BIRTHING_IN_WATER_OTHER_LINK_ID,
  BIRTHING_NOTES_LINK_ID,
  BIRTHING_POSITION_LINK_ID,
  BIRTHING_TIME_LINK_ID,
  BIRTHING_TYPE_LINK_ID,
} from "@/encounters/components/EncounterLabor/flowsheet-forms/birthing/constants/birthing-link-ids";
import type { BirthingFlowsheetDrawerMode } from "@/encounters/components/EncounterLabor/flowsheet-forms/birthing/types/birthing-drawer";
import type { BirthingFlowsheetFormValues } from "@/encounters/components/EncounterLabor/flowsheet-forms/birthing/types/birthing-form";
import { isBirthingDeliveryMethodOtherSelected } from "@/encounters/components/EncounterLabor/flowsheet-forms/birthing/utils/birthing-questionnaire-mapping";
import { valueSetContainsToNativeSelectOptions } from "@/encounters/components/EncounterLabor/flowsheet-forms/shared/labor-flowsheet-coding-select";
import { LABOR_FLOWSHEET_DRAWER_MODES } from "@/encounters/components/EncounterLabor/flowsheet-forms/shared/labor-flowsheet-drawer-constants";
import { LaborFlowsheetDateTimeFields } from "@/encounters/components/EncounterLabor/flowsheet-forms/shared/LaborFlowsheetDateTimeFields";
import { LaborFlowsheetSignatureInitialsField } from "@/encounters/components/EncounterLabor/flowsheet-forms/shared/LaborFlowsheetSignatureInitialsField";
import { BirthingComponent, QuiltedHealthValueSets } from "@/fhir/enums/quilted-health";
import { Systems } from "@/fhir/enums/systems";
import { questionnaireFieldTestId } from "@/intake-forms/utils/questionnaire-field-testid";
import { getValueSetOptions } from "@/utils/medplum";
import { toast } from "@/utils/notifications";

const BIRTHING_DELIVERY_METHOD_OPTIONS = valueSetContainsToNativeSelectOptions([
  { system: Systems.LOINC, code: "LA20039-6", display: "Vaginal" },
  { system: Systems.LOINC, code: "LA20042-0", display: "Cesarean" },
  { system: Systems.QH, code: BirthingComponent.DeliveryMethodOther, display: OTHER_DISPLAY },
]);
const BIRTHING_DELIVERY_DATE_TIME_FIELDS = [
  { label: "Crown Delivered", linkId: BIRTHING_CROWN_DATE_TIME_LINK_ID },
  { label: "Head Delivered", linkId: BIRTHING_HEAD_DATE_TIME_LINK_ID },
  { label: "Body Delivered", linkId: BIRTHING_BODY_DATE_TIME_LINK_ID },
] as const satisfies { label: string; linkId: keyof BirthingFlowsheetFormValues }[];
type ValueSetOptions = ValueSetExpansionContains[];

interface BirthingFormFieldsProps {
  form: UseFormReturnType<BirthingFlowsheetFormValues>;
  clientId: string;
  mode?: BirthingFlowsheetDrawerMode;
  isDrawerOpened?: boolean;
}

export function BirthingFormFields({
  form,
  clientId,
  mode = LABOR_FLOWSHEET_DRAWER_MODES.QuickAdd,
  isDrawerOpened = true,
}: BirthingFormFieldsProps) {
  const medplum = useMedplum();
  const [typeOptions, setTypeOptions] = useState<ValueSetOptions>([]);
  const [positionOptions, setPositionOptions] = useState<ValueSetOptions>([]);
  const [fetalPresentationOptions, setFetalPresentationOptions] = useState<ValueSetOptions>([]);
  const showInWaterOther = isOtherDisplaySelected(form.values.birthingInWater);
  const showDeliveryMethodOther = isBirthingDeliveryMethodOtherSelected(
    form.values.birthingDeliveryMethod,
  );
  const isQuickAddMode = mode === LABOR_FLOWSHEET_DRAWER_MODES.QuickAdd;

  useEffect(() => {
    if (!isDrawerOpened) {
      return;
    }

    let isCancelled = false;

    const loadValueSetOptions = async () => {
      const valueSetRequests = [
        {
          label: "Type",
          valueSet: QuiltedHealthValueSets.BirthingType,
          setOptions: setTypeOptions,
        },
        {
          label: "Position",
          valueSet: QuiltedHealthValueSets.LaborPosition,
          setOptions: setPositionOptions,
        },
        {
          label: "Fetal Presentation",
          valueSet: QuiltedHealthValueSets.FetalPresentation,
          setOptions: setFetalPresentationOptions,
        },
      ] as const;

      const results = await Promise.allSettled(
        valueSetRequests.map(({ valueSet }) => getValueSetOptions(medplum, valueSet)),
      );

      if (isCancelled) {
        return;
      }

      const failedLabels: string[] = [];
      results.forEach((result, index) => {
        const { label, setOptions } = valueSetRequests[index];
        if (result.status === "fulfilled") {
          setOptions(result.value ?? []);
          return;
        }
        console.error(`Error loading Birthing ${label} value set:`, result.reason);
        failedLabels.push(label);
      });

      if (failedLabels.length > 0) {
        toast({
          type: "error",
          title: "Failed to load form options",
          message: `${failedLabels.join(", ")} options could not be loaded. Try closing and reopening the drawer.`,
          autoClose: false,
        });
      }
    };

    void loadValueSetOptions();

    return () => {
      isCancelled = true;
    };
  }, [isDrawerOpened, medplum]);

  return (
    <Stack gap="lg">
      <LaborFlowsheetDateTimeFields
        form={form}
        dateFieldName={BIRTHING_DATE_LINK_ID}
        timeFieldName={BIRTHING_TIME_LINK_ID}
        autoPopulateDateTime={isQuickAddMode}
        dateTimeGroupLinkId={BIRTHING_DATE_TIME_LINK_ID}
      />

      {BIRTHING_DELIVERY_DATE_TIME_FIELDS.map(({ label, linkId }) => (
        <DateTimePickerNowField
          form={form}
          key={linkId}
          name={linkId}
          label={label}
          dataTestId={questionnaireFieldTestId(linkId)}
        />
      ))}

      <BirthingPostpartumFlowsheetNoticeField bodyDeliveredAt={form.values.birthingBodyDateTime} />

      <ShoulderDystociaDisplay />

      <NativeSelect
        label="Type"
        data-path={BIRTHING_TYPE_LINK_ID}
        data={valueSetContainsToNativeSelectOptions(typeOptions)}
        {...form.getInputProps(BIRTHING_TYPE_LINK_ID)}
        value={form.getInputProps(BIRTHING_TYPE_LINK_ID).value ?? ""}
        data-testid={questionnaireFieldTestId(BIRTHING_TYPE_LINK_ID)}
      />

      <NativeSelect
        label="Client's Position"
        data-path={BIRTHING_POSITION_LINK_ID}
        data={valueSetContainsToNativeSelectOptions(positionOptions)}
        {...form.getInputProps(BIRTHING_POSITION_LINK_ID)}
        value={form.getInputProps(BIRTHING_POSITION_LINK_ID).value ?? ""}
        data-testid={questionnaireFieldTestId(BIRTHING_POSITION_LINK_ID)}
      />

      <ClearableYesNoRadioField
        form={form}
        label="Client in Water"
        name={BIRTHING_IN_WATER_LINK_ID}
        options={yesNoOtherRadioOptions}
        dataPath={BIRTHING_IN_WATER_LINK_ID}
        dataTestId={questionnaireFieldTestId(BIRTHING_IN_WATER_LINK_ID)}
        onClear={() => form.setFieldValue(BIRTHING_IN_WATER_OTHER_LINK_ID, "")}
      />

      {showInWaterOther ? (
        <TextInput
          label="Other"
          placeholder="Please Specify Other"
          data-path={BIRTHING_IN_WATER_OTHER_LINK_ID}
          {...form.getInputProps(BIRTHING_IN_WATER_OTHER_LINK_ID)}
          data-testid={questionnaireFieldTestId(BIRTHING_IN_WATER_OTHER_LINK_ID)}
        />
      ) : null}

      <NativeSelect
        label="Fetal Presentation"
        data-path={BIRTHING_FETAL_PRESENTATION_LINK_ID}
        data={valueSetContainsToNativeSelectOptions(fetalPresentationOptions)}
        {...form.getInputProps(BIRTHING_FETAL_PRESENTATION_LINK_ID)}
        value={form.getInputProps(BIRTHING_FETAL_PRESENTATION_LINK_ID).value ?? ""}
        data-testid={questionnaireFieldTestId(BIRTHING_FETAL_PRESENTATION_LINK_ID)}
      />

      <NativeSelect
        label="Method of Delivery"
        data={BIRTHING_DELIVERY_METHOD_OPTIONS}
        data-path={BIRTHING_DELIVERY_METHOD_LINK_ID}
        {...form.getInputProps(BIRTHING_DELIVERY_METHOD_LINK_ID)}
        value={form.getInputProps(BIRTHING_DELIVERY_METHOD_LINK_ID).value ?? ""}
        data-testid={questionnaireFieldTestId(BIRTHING_DELIVERY_METHOD_LINK_ID)}
      />

      {showDeliveryMethodOther ? (
        <TextInput
          label="Other"
          placeholder="Please Specify Other"
          data-path={BIRTHING_DELIVERY_METHOD_OTHER_LINK_ID}
          {...form.getInputProps(BIRTHING_DELIVERY_METHOD_OTHER_LINK_ID)}
          data-testid={questionnaireFieldTestId(BIRTHING_DELIVERY_METHOD_OTHER_LINK_ID)}
        />
      ) : null}

      <Textarea
        label="Notes"
        data-path={BIRTHING_NOTES_LINK_ID}
        {...form.getInputProps(BIRTHING_NOTES_LINK_ID)}
        data-testid={questionnaireFieldTestId(BIRTHING_NOTES_LINK_ID)}
      />

      <LaborFlowsheetSignatureInitialsField
        form={form}
        clientId={clientId}
        label="Sign Initials"
        name="birthingSignatureInitials"
        autoPopulateSignature={isQuickAddMode}
      />
    </Stack>
  );
}
